/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData, resolveOwnerSlug } from "@cosmos/hygraph-data";
import { Locale, CosmosPageBlogDetailMetadataQuery } from "@cosmos/hygraph-schema";
import { getSlugParam, getHygraphLocale } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { Metadata } from "next";
import { notFound } from "next/navigation";

import { CosmosPageMetadata } from "../common/page.types";
import { checkValidParams } from "../templates/selector";

const getPageMetadata = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
	ownerId?: string,
	endpoint?: string,
): Promise<CosmosPageBlogDetailMetadataQuery> => {
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	if (!slug) {
		throw new Error("Missing slug");
	}

	// Transform URL path to Hygraph slug by applying owner prefix
	const hygraphSlug = await resolveOwnerSlug(slug, preview, ownerId, endpoint);

	const resp = await getHygraphCosmosPageData(
		"CosmosPageBlogDetailMetadata",
		{
			locale: [locale, Locale.En] as Locale[],
			slug: hygraphSlug,
		},
		preview,
		endpoint,
	);

	return resp;
};

export async function cosmosPageBlogDetailMetadata({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	getAppMetadata,
	hygraphEndpoint,
	hygraphOwnerId,
}: CosmosPageMetadata): Promise<Metadata> {
	const valid = checkValidParams({ params, searchParams });
	if (!valid) {
		return notFound();
	}

	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;
	const locale = params.lang;
	let pageData: CosmosPageBlogDetailMetadataQuery = {};
	try {
		pageData = await getPageMetadata(
			getHygraphLocale(hygraphLocales, locale),
			slug,
			searchParams.preview,
			hygraphOwnerId,
			hygraphEndpoint,
		);
	} catch (e) {
		console.error("Failed to get cosmosPageBlogDetailMetadata()", { error: isError(e) && e.message });
	}
	const metaTags = pageData.cosmosPageBlogDetail?.metaTags || {};

	return getAppMetadata(metaTags, slug, locale);
}
